import { lazy, Suspense, useMemo } from 'react';
import { Redirect, Route as BaseRoute, Switch } from 'react-router-dom';
import {
  ErrorBoundaryFallback,
  ErrorPage,
  FullScreenSpin,
} from '@distribusion/portal-lib/components';
import { PerformanceMarksEnum, useFramePaint } from '@distribusion/portal-lib/metrics';
import { useAntLocale } from '@distribusion/portal-lib/translation';
import * as Sentry from '@sentry/react';
import ConfigProvider from 'antd/es/config-provider';
import { PrivateRoute } from 'components';
import { ROUTES } from 'constants/common';
import { AuthController } from 'features/auth';
import { Layout } from 'features/layout';
import { SettingsController } from 'features/settings';
import { detectDatalogicBrowser } from 'utils/detect-datalogic-browser';

interface ScriptExternalLoadError extends Error {
  request: string;
}

const Route = Sentry.withSentryRouting(BaseRoute);

const handleChunkLoadingError = (e: ScriptExternalLoadError) => {
  console.error(e);
  Sentry.captureException(e);
  return { default: ErrorPage };
};

const AgencyManagement = lazy(() =>
  import('@portal/agency-management').catch(handleChunkLoadingError),
);
const BookingManagement = lazy(() =>
  import('@portal/booking-management').catch(handleChunkLoadingError),
);
const Dashboards = lazy(() => import('@portal/dashboards').catch(handleChunkLoadingError));
const Finances = lazy(() => import('@portal/finances').catch(handleChunkLoadingError));
const TradingDesk = lazy(() => import('@portal/trading-desk').catch(handleChunkLoadingError));
const Pricing = lazy(() => import('@portal/pricing').catch(handleChunkLoadingError));
const Configuration = lazy(() => import('@portal/configuration').catch(handleChunkLoadingError));

const LoginPage = lazy(() => import('features/auth/login-page').catch(handleChunkLoadingError));
const RecoveryPage = lazy(() =>
  import('features/auth/recovery-page').catch(handleChunkLoadingError),
);

const Users = lazy(() => import('features/users/app').catch(handleChunkLoadingError));

const App = () => {
  const locale = useAntLocale();
  const isDatalogicBrowser = useMemo(() => detectDatalogicBrowser(), []);

  useFramePaint({
    enabled: true,
    mark: PerformanceMarksEnum.LayoutFCP,
  });

  return (
    <Sentry.ErrorBoundary fallback={ErrorBoundaryFallback}>
      <ConfigProvider locale={locale}>
        <Suspense fallback={<FullScreenSpin />}>
          <Switch>
            <Route exact path={ROUTES.LOGIN} component={LoginPage} />
            <Route exact path={ROUTES.RECOVERY} component={RecoveryPage} />
            <Route path="*">
              <AuthController>
                <SettingsController>
                  <Layout>
                    <Suspense fallback={<FullScreenSpin />}>
                      <Switch>
                        {isDatalogicBrowser ? (
                          <Redirect
                            exact
                            path={ROUTES.HOME}
                            to={ROUTES.BOOKING_MANAGEMENT.TICKET_VALIDATOR}
                          />
                        ) : (
                          <Redirect exact path={ROUTES.HOME} to={ROUTES.DASHBOARDS.WELCOME} />
                        )}
                        <Switch>
                          <PrivateRoute
                            path={ROUTES.AGENCY_MANAGEMENT.INDEX}
                            component={AgencyManagement}
                          />
                          <PrivateRoute
                            path={ROUTES.BOOKING_MANAGEMENT.INDEX}
                            component={BookingManagement}
                          />
                          <PrivateRoute path={ROUTES.DASHBOARDS.INDEX} component={Dashboards} />
                          <PrivateRoute path={ROUTES.FINANCES.INDEX} component={Finances} />
                          <PrivateRoute path={ROUTES.TRADING_DESK.INDEX} component={TradingDesk} />
                          <PrivateRoute path={ROUTES.PRICING.INDEX} component={Pricing} />
                          <PrivateRoute
                            path={ROUTES.CONFIGURATION.INDEX}
                            component={Configuration}
                          />
                          <PrivateRoute path={ROUTES.USERS.INDEX} component={Users} />
                          <Route path="*" component={() => <ErrorPage code={404} />} />
                        </Switch>
                      </Switch>
                    </Suspense>
                  </Layout>
                </SettingsController>
              </AuthController>
            </Route>
          </Switch>
        </Suspense>
      </ConfigProvider>
    </Sentry.ErrorBoundary>
  );
};

export default App;
